import { useNavigate, useLocation, Link } from 'react-router-dom'
import logImg from '../../assets/img/logo-img.png'
import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../api/axios';
import { useAuthApi } from '../../hooks/auth/useAuthApi';
import { toast, ToastContainer } from 'react-toastify';
import SafeImage from '../../components/common/SafeImage';

export default function Login() {
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [errors, setErrors] = useState({})
    const [formError, setFormError] = useState('')
    const navigate = useNavigate()
    const location = useLocation()
    const queryClient = useQueryClient();
    const { login } = useAuthApi();

    const [showPassword, setShowPassword] = useState(false);


    const params = new URLSearchParams(location.search);
    const fromParam = params.get("from");

    const loginMutation = useMutation({
        mutationFn: async ({ email, password }) => {
            const response = await apiClient.post('/login-dashboard', { email, password });
            return response.data;
        },
        onSuccess: (data) => {
            // Set user data in cache immediately
            if (data.success && data.data?.user) {
                queryClient.setQueryData(['user'], data.data.user);
            }
            // Also invalidate to ensure fresh data
            queryClient.invalidateQueries(['user']);
        },
    });

    const validateForm = () => {
        const newErrors = {};

        // Email validation
        if (!email) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        // Password validation
        if (!password) {
            newErrors.password = 'Password is required';
        } else if (password.length < 6) {
            newErrors.password = 'Password must be at least 6 characters';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const LoginUser = async (e) => {
        e.preventDefault();
        setFormError(''); // Clear any previous form errors

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await loginMutation.mutateAsync({ email, password });
            if (response && response.success) {
                const { user } = response.data;
                // Save JWT token for axios Authorization header as fallback (cookie is primary)
                if (response.data.token) {
                    sessionStorage.setItem('token', response.data.token);
                }
                if (user && user.role) {


                    // Small delay to ensure authentication state is set
                    await new Promise(resolve => setTimeout(resolve, 100));

                    // Check for intended redirect path
                    const params = new URLSearchParams(location.search);
                    const fromQuery = params.get('from');
                    const fromState = location.state?.from;
                    const intendedPath = fromState || fromQuery;


                    if (intendedPath) {
                        // Redirect to intended path
                        navigate(intendedPath, { replace: true });
                    } else {
                        // Default role-based navigation
                        if (user.role === 'admin') {
                            navigate('/dashboard', { replace: true });
                        } else if (user.role === 'employee') {
                            navigate('/employee-dashboard', { replace: true });
                        } else if (user.role === 'vendor') {
                            navigate('/vendor-dashboard', { replace: true });
                        }
                    }
                }
            } else {
                setFormError(response?.message || 'Login failed. Please check your credentials.');
            }
        } catch (err) {
            setFormError(err.response?.data?.message || err.message || 'An unexpected error occurred.');
        } finally {
            setIsSubmitting(false);
        }
    }

    useEffect(() => {
        if (fromParam) {
            toast.error("Login has expired, please log in again");
        }
    }, [])

    return <>
        {/* <ToastContainer/> */}
        {/* Preload critical assets */}
        <link rel="preload" as="image" href={logImg} />
        <link rel="preload" as="font" href="https://fonts.googleapis.com/css2?family=Public+Sans:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap" crossOrigin="anonymous" />

        <style>
            {`
                .spinner {
                    width: 20px;
                    height: 20px;
                    border: 3px solid #f3f3f3;
                    border-top: 3px solid #3498db;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                    display: inline-block;
                    margin-right: 8px;
                }
                
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                
                .login-btn {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    min-height: 45px;
                }

                .error-message {
                    color: #dc3545;
                    font-size: 0.875rem;
                    margin-top: 0.25rem;
                }

                .form-error {
                    color: #dc3545;
                    font-size: 0.875rem;
                    margin-top: 1rem;
                    padding: 0.75rem;
                    background-color: #f8d7da;
                    border: 1px solid #f5c2c7;
                    border-radius: 0.25rem;
                    text-align: center;
                }

                .form-control.is-invalid {
                    border-color: #dc3545;
                    padding-right: calc(1.5em + 0.75rem);
                    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12' width='12' height='12' fill='none' stroke='%23dc3545'%3e%3ccircle cx='6' cy='6' r='4.5'/%3e%3cpath stroke-linejoin='round' d='M5.8 3.6h.4L6 6.5z'/%3e%3ccircle cx='6' cy='8.2' r='.6' fill='%23dc3545' stroke='none'/%3e%3c/svg%3e");
                    background-repeat: no-repeat;
                    background-position: right calc(0.375em + 0.1875rem) center;
                    background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem);
                }

                .logo-img {
                    width: 130px;
                    height: 100px;
                    object-fit: contain;
                    margin: -1rem 0;
                    aspect-ratio: 130/100;
                }
            `}
        </style>

        {/* <!-- Google font--> */}
        <link
            href="https://fonts.googleapis.com/css2?family=Public+Sans:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap"
            rel="stylesheet" />

        {/* <!-- login section start --> */}
        <section className="log-in-section section-b-space">
            <div className="container-fluid">
                <div className="row justify-content-center">
                    <div className="col-xl-4 col-lg-5">
                        <div className="log-in-box">
                            <div className="log-in-title">
                                <h3>Welcome To Go home express</h3>
                                <h4>Log In Your Account</h4>
                            </div>

                            <div className="input-box">
                                <form onSubmit={LoginUser} className="row g-4">
                                    <div className="col-12">
                                        <div className="form-floating theme-form-floating log-in-form">
                                            <input
                                                value={email}
                                                onChange={(e) => {
                                                    setEmail(e.target.value);
                                                    if (errors.email) {
                                                        setErrors(prev => ({ ...prev, email: '' }));
                                                    }
                                                    setFormError(''); // Clear form error when user types
                                                }}
                                                type="email"
                                                className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                                                id="email"
                                            // placeholder="Email Address" 
                                            />
                                            <label htmlFor="email">Email Address</label>
                                            {errors.email && <div className="error-message">{errors.email}</div>}
                                        </div>
                                    </div>

                                    {/* <div className="col-12">
                                        <div className="form-floating theme-form-floating log-in-form">
                                            <input 
                                                type="password" 
                                                className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                                                id="password"
                                                value={password} 
                                                onChange={(e) => {
                                                    setPassword(e.target.value);
                                                    if (errors.password) {
                                                        setErrors(prev => ({ ...prev, password: '' }));
                                                    }
                                                    setFormError(''); // Clear form error when user types
                                                }}
                                                placeholder="Password" 
                                            />
                                            <label htmlFor="password">Password</label>
                                            {errors.password && <div className="error-message">{errors.password}</div>}
                                        </div>
                                    </div> */}

                                    <div className="col-12">
                                        <div
                                            className="form-floating theme-form-floating log-in-form"
                                            style={{ position: "relative" }}
                                        >
                                            <input
                                                type={showPassword ? "text" : "password"}
                                                className={`form-control ${errors.password ? "is-invalid" : ""}`}
                                                id="password"
                                                value={password}
                                                onChange={(e) => {
                                                    setPassword(e.target.value);
                                                    if (errors.password) {
                                                        setErrors((prev) => ({ ...prev, password: "" }));
                                                    }
                                                    setFormError("");
                                                }}
                                                //   placeholder="Password"
                                                style={{ paddingRight: "45px" }}
                                            />

                                            <label htmlFor="password">Password</label>

                                            {/* Eye Icon Button */}
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                style={{
                                                    position: "absolute",
                                                    right: "12px",
                                                    top: "50%",
                                                    transform: "translateY(-50%)",
                                                    border: "none",
                                                    background: "none",
                                                    cursor: "pointer",
                                                    color: "#6c757d",
                                                    zIndex: 10,
                                                }}
                                            >
                                                <i className={`fa ${showPassword ? "fa-eye-slash" : "fa-eye"}`}></i>
                                            </button>

                                            {errors.password && (
                                                <div className="error-message">{errors.password}</div>
                                            )}
                                        </div>
                                    </div>


                                    <div className="col-12">
                                        <div className="forgot-box">
                                            <div className="form-check ps-0 m-0 remember-box">

                                            </div>
                                            <Link to="/forgot" className="forgot-password">Forgot Password?</Link>
                                        </div>
                                    </div>

                                    {formError && (
                                        <div className="col-12">
                                            <div className="form-error">
                                                {formError}
                                            </div>
                                        </div>
                                    )}

                                    <div className="col-12">
                                        <button
                                            type="submit"
                                            className="btn btn-animation w-100 login-btn"
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <div className="spinner"></div>
                                                    <span>Logging in...</span>
                                                </>
                                            ) : 'Log In'}
                                        </button>
                                    </div>
                                </form>
                            </div>

                            {/* <div className="other-log-in">
                                <h6>or</h6>
                            </div> */}

                            {/* <div className="log-in-button">
                                <ul>
                                    <li>
                                        <a href="https://www.google.com/" className="btn google-button w-100">
                                            <img src="../public/assets/images/inner-page/google.png" className="blur-up lazyload"
                                                alt="" /> Log In with Google
                                        </a>
                                    </li>
                                    <li>
                                        <a href="https://www.facebook.com/" className="btn google-button w-100">
                                            <img src="../public/assets/images/inner-page/facebook.png" className="blur-up lazyload"
                                                alt="" /> Log In with Facebook
                                        </a>
                                    </li>
                                </ul>
                            </div> */}
                        </div>
                    </div>
                </div>
            </div>
            <div className="logo-container">
                <a href="" className="logo-login">
                    <SafeImage
                        src={logImg}
                        className="img-fluid logo-img"
                        alt="Go Home Express Logo"
                        width="130"
                        height="100"
                        loading="eager"
                    />
                </a>
            </div>
        </section>
        {/* <!-- login section end --> */}
    </>
}